import { ChangeDetectionStrategy, Component, EventEmitter, forwardRef, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CheckBoxModule } from '@syncfusion/ej2-angular-buttons';
import { DropDownListModule } from '@syncfusion/ej2-angular-dropdowns';
import { NumericTextBoxModule } from '@syncfusion/ej2-angular-inputs';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { DynamicAutocompleteComponent } from 'src/app/base/shared/dynamic-autocomplete/dynamic-autocomplete.component';
import { IDynamicAutocompleteItem } from 'src/app/base/shared/dynamic-autocomplete/dynamic-autocomplete-item';
import {
  FilterDTO,
  FilterOperator,
  SamplingProfileSignalConfigDTO,
  SignalAvailabilityDTO,
  SignalAvailabilityStatus,
  SignalCollectionMode,
  SortDirection,
  VehicleListDTO,
  VehicleSignalAvailabilityDTO,
} from 'src/app/base/models/models.swagger.generated';
import { SignalCatalogLookupService } from '../signal-catalog-lookup.service';

const DefaultSamplingIntervalMs = 1000;
const CollectionModeTranslationBase = 'i18n.shared.enums.SignalCollectionMode.';
const AvailabilityStatusTranslationBase = 'i18n.shared.enums.SignalAvailabilityStatus.';

interface CollectionModeOption {
  value: SignalCollectionMode;
  label: string;
}

interface VehicleOption {
  id: number;
  label: string;
}

/**
 * Editor for a sampling profile's signal list, and the place where signal availability surfaces.
 *
 * Availability is measured per collector installation, so a verdict only means something for one
 * specific vehicle. The vehicle selector above the list picks which of the profile's assigned vehicles
 * the badges describe; with no assigned vehicle there is nothing to show and the selector is replaced
 * by an explanatory empty state.
 */
@Component({
  selector: 'app-sampling-profile-signals-editor',
  templateUrl: './sampling-profile-signals-editor.component.html',
  styleUrls: ['./sampling-profile-signals-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CheckBoxModule, DropDownListModule, NumericTextBoxModule, DynamicAutocompleteComponent, ReactiveFormsModule, TranslatePipe],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SamplingProfileSignalsEditorComponent),
      multi: true,
    },
  ],
})
export class SamplingProfileSignalsEditorComponent implements ControlValueAccessor, OnInit, OnChanges, OnDestroy {

  /** Vehicles running this profile. Empty for a new profile, or one nobody uses yet. */
  @Input() public assignedVehicles: VehicleListDTO[] = [];

  /** Availability for the currently selected vehicle, or null when none is selected. */
  @Input() public availability: VehicleSignalAvailabilityDTO | null = null;

  @Input() public availabilityLoading = false;

  /** True for a shared System profile — drives the warning banner. */
  @Input() public isSystemProfile = false;

  @Input() public selectedVehicleId: number | null = null;

  /** Raised when the user picks a different vehicle to show availability for. */
  @Output() public readonly selectedVehicleIdChange = new EventEmitter<number | null>();

  public signals: SamplingProfileSignalConfigDTO[] = [];
  public disabled = false;

  public readonly vehicleFields = { text: 'label', value: 'id' };
  public vehicleOptions: VehicleOption[] = [];

  public readonly collectionModeFields = { text: 'label', value: 'value' };
  public readonly collectionModeOptions: CollectionModeOption[];

  /**
   * Standalone control for the "add signal" picker. A reactive control rather than ngModel, because
   * this editor renders inside the parent form's `formGroup` context, where a bare ngModel throws.
   */
  public readonly signalPickerControl = new FormControl<number | null>(null);

  private readonly destroyed = new Subject<void>();

  /** Availability keyed by SignalId, rebuilt whenever the input changes. */
  private availabilityBySignalId = new Map<number, SignalAvailabilityDTO>();

  /** Names seen in picker results, so a freshly added row shows a name without a reload. */
  private readonly signalNameCache = new Map<number, string>();

  private onChangeCallback: (value: SamplingProfileSignalConfigDTO[]) => void = () => { };
  private onTouchedCallback: () => void = () => { };

  constructor(
    private signalCatalogLookupService: SignalCatalogLookupService,
    private translateService: TranslateService,
  ) {
    this.collectionModeOptions = Object.keys(SignalCollectionMode)
      .filter(key => isNaN(Number(key)))
      .map(key => ({
        value: (SignalCollectionMode as unknown as Record<string, SignalCollectionMode>)[key],
        label: this.translateService.instant(CollectionModeTranslationBase + key),
      }));
  }

  public ngOnInit(): void {
    this.signalPickerControl.valueChanges
      .pipe(takeUntil(this.destroyed))
      .subscribe(signalId => this.onSignalPicked(signalId));
  }

  public ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['availability']) {
      this.availabilityBySignalId = new Map(
        (this.availability?.signals ?? []).map(signal => [signal.signalId!, signal]));
    }

    if (changes['assignedVehicles']) {
      this.vehicleOptions = (this.assignedVehicles ?? []).map(vehicle => ({
        id: vehicle.id!,
        label: vehicle.vin || vehicle.model || `#${vehicle.id}`,
      }));
    }
  }

  // #region ControlValueAccessor

  public writeValue(value: SamplingProfileSignalConfigDTO[] | null): void {
    // Copy so row edits never mutate the array the parent form still holds.
    this.signals = (value ?? []).map(signal => new SamplingProfileSignalConfigDTO({ ...signal }));
  }

  public registerOnChange(fn: (value: SamplingProfileSignalConfigDTO[]) => void): void {
    this.onChangeCallback = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouchedCallback = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // #endregion

  // #region Signal list editing

  /** Feeds the signal picker, caching names so newly added rows are immediately readable. */
  public searchSignals = (searchText: string): Observable<IDynamicAutocompleteItem[]> => {
    const filters: FilterDTO[] = searchText
      ? [new FilterDTO({ isComplex: false, field: 'Name', operator: FilterOperator.Contains, value: searchText })]
      : [];

    return this.signalCatalogLookupService.getData(0, 20, filters, 'Name', SortDirection.Ascending).pipe(
      map(result => (result?.items ?? [])
        // Already-configured signals are not offered again — SignalId is unique per profile.
        .filter(definition => !this.signals.some(signal => signal.signalId === definition.signalId))
        .map(definition => {
          const label = definition.displayName || definition.name || `#${definition.signalId}`;
          this.signalNameCache.set(definition.signalId!, label);
          return { id: definition.signalId!, name: label } as IDynamicAutocompleteItem;
        })),
    );
  };

  public onSignalPicked(signalId: number | null): void {
    if (!signalId || this.signals.some(signal => signal.signalId === signalId)) {
      return;
    }

    // Clear the picker so the same control can add the next signal. Emit-less to avoid re-entering
    // this handler via valueChanges.
    this.signalPickerControl.setValue(null, { emitEvent: false });

    this.signals = [
      ...this.signals,
      new SamplingProfileSignalConfigDTO({
        id: 0,
        signalId,
        signalName: this.signalNameCache.get(signalId),
        enabled: true,
        collectionMode: SignalCollectionMode.Periodic,
        samplingIntervalMs: DefaultSamplingIntervalMs,
      }),
    ];

    this.emitChange();
  }

  public removeSignal(signalId: number): void {
    this.signals = this.signals.filter(signal => signal.signalId !== signalId);
    this.emitChange();
  }

  public onEnabledChanged(signal: SamplingProfileSignalConfigDTO, enabled: boolean): void {
    signal.enabled = enabled;
    this.emitChange();
  }

  public onCollectionModeChanged(signal: SamplingProfileSignalConfigDTO, mode: SignalCollectionMode): void {
    signal.collectionMode = mode;
    // Only Periodic carries an interval. The backend normalizes this too, but keeping the UI honest
    // avoids showing a value that is about to be discarded.
    signal.samplingIntervalMs = mode === SignalCollectionMode.Periodic
      ? (signal.samplingIntervalMs ?? DefaultSamplingIntervalMs)
      : undefined;
    this.emitChange();
  }

  public onIntervalChanged(signal: SamplingProfileSignalConfigDTO, intervalMs: number | null): void {
    signal.samplingIntervalMs = intervalMs ?? undefined;
    this.emitChange();
  }

  public isIntervalEditable(signal: SamplingProfileSignalConfigDTO): boolean {
    return !this.disabled && signal.collectionMode === SignalCollectionMode.Periodic;
  }

  public signalLabel(signal: SamplingProfileSignalConfigDTO): string {
    return signal.displayName || signal.signalName || `#${signal.signalId}`;
  }

  // #endregion

  // #region Availability

  public get hasAvailabilityContext(): boolean {
    return this.vehicleOptions.length > 0;
  }

  public onVehicleSelected(vehicleId: number | null): void {
    this.selectedVehicleIdChange.emit(vehicleId);
  }

  public availabilityOf(signal: SamplingProfileSignalConfigDTO): SignalAvailabilityDTO | undefined {
    return this.availabilityBySignalId.get(signal.signalId!);
  }

  public availabilityLabelKey(signal: SamplingProfileSignalConfigDTO): string {
    const status = this.availabilityOf(signal)?.status ?? SignalAvailabilityStatus.Unknown;
    return AvailabilityStatusTranslationBase + SignalAvailabilityStatus[status];
  }

  public availabilityBadgeClass(signal: SamplingProfileSignalConfigDTO): string {
    switch (this.availabilityOf(signal)?.status ?? SignalAvailabilityStatus.Unknown) {
      case SignalAvailabilityStatus.Available:
        return 'badge bg-success';
      case SignalAvailabilityStatus.NoResponse:
        return 'badge bg-danger';
      case SignalAvailabilityStatus.ParseError:
      case SignalAvailabilityStatus.OutOfRange:
      case SignalAvailabilityStatus.Intermittent:
        return 'badge bg-warning text-dark';
      default:
        return 'badge bg-secondary';
    }
  }

  /** Counters, timestamps and any collector-supplied detail, shown as the badge's tooltip. */
  public availabilityTooltip(signal: SamplingProfileSignalConfigDTO): string {
    const availability = this.availabilityOf(signal);
    if (!availability) {
      return '';
    }

    const parts: string[] = [];

    if (availability.attemptCount) {
      parts.push(this.translateService.instant('i18n.domain.sampling-profiles.availability.attempts', {
        successCount: availability.successCount ?? 0,
        attemptCount: availability.attemptCount,
      }));
    }
    if (availability.lastOkUtc) {
      parts.push(this.translateService.instant('i18n.domain.sampling-profiles.availability.last-ok', {
        date: availability.lastOkUtc.toFormat('dd.MM.yyyy HH:mm'),
      }));
    }
    if (availability.lastEvaluatedUtc) {
      parts.push(this.translateService.instant('i18n.domain.sampling-profiles.availability.last-evaluated', {
        date: availability.lastEvaluatedUtc.toFormat('dd.MM.yyyy HH:mm'),
      }));
    }
    if (availability.detail) {
      parts.push(availability.detail);
    }
    if (availability.isStale) {
      parts.push(this.translateService.instant('i18n.domain.sampling-profiles.availability.stale-hint'));
    }

    return parts.join(' · ');
  }

  public isStale(signal: SamplingProfileSignalConfigDTO): boolean {
    return this.availabilityOf(signal)?.isStale === true;
  }

  // #endregion

  private emitChange(): void {
    this.onTouchedCallback();
    // New array reference so the parent form control registers the change and marks itself dirty.
    this.signals = [...this.signals];
    this.onChangeCallback(this.signals);
  }
}
